import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

/**
 * T10A-001 — route-level error boundary for `/approvals/:id`.
 *
 * The global `Sentry.ErrorBoundary` in `main.tsx` dead-ends on a bare
 * "Something went wrong." div with no recovery path — once the
 * approval detail render threw (the `standardTotalDiscountPct`
 * undefined-read), the user was stranded on a white screen with no
 * way back. This boundary scopes the recovery affordance to the
 * approval surface: any render error inside the detail tree shows a
 * graceful fallback with a **Back to Approvals** link, so the route
 * is never an unrecoverable dead-end again.
 *
 * No shared `ErrorBoundary` exists in `apps/crm` or `packages/shared`
 * (only Sentry's global one), so this is a small local class
 * component — the only idiomatic way to catch render errors in React.
 */

type Props = {
  children: ReactNode;
};

type State = {
  hasError: boolean;
};

export class ApprovalDetailErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface to the console for local debugging; the global Sentry
    // boundary higher in the tree is no longer the one rendering, so
    // we log here to keep error visibility.
    // eslint-disable-next-line no-console
    console.error('ApprovalDetail render error', error, info.componentStack);
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <main className="mx-auto max-w-3xl px-4 py-6 sm:px-6 sm:py-8">
          <div
            role="alert"
            className="rounded border border-rr-border-default bg-rr-surface-raised p-4 text-sm text-rr-error"
          >
            <h1 className="text-base font-semibold">Something went wrong loading this approval</h1>
            <p className="mt-1 text-rr-error">
              We couldn&rsquo;t display this approval. This has been logged. You can head back to the
              approvals queue and try another, or reload to retry.
            </p>
            <div className="mt-3 flex flex-wrap gap-3">
              <Link
                to="/approvals"
                className="rounded border border-rr-accent-primary bg-rr-accent-primary px-3 py-1.5 text-sm font-medium text-rr-on-accent hover:opacity-90"
              >
                ← Back to Approvals
              </Link>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="rounded border border-rr-border-default px-3 py-1.5 text-sm font-medium text-rr-fg-primary hover:bg-rr-surface-raised"
              >
                Reload
              </button>
            </div>
          </div>
        </main>
      );
    }

    return this.props.children;
  }
}
